import { apiContract } from '@kidshub/contracts';
import { createORPCClient } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { environment } from './environment.js';

/**
 * Builds a typed client from the shared contract.
 *
 * Nothing here restates the shape of an endpoint. The types come from
 * `@kidshub/contracts`, which the backend is also checked against, so a change
 * to a response breaks this build rather than surfacing at runtime.
 *
 * An OpenAPI link rather than an RPC one, because the backend serves real REST
 * routes: `SPEC.md §37` asks for versioned resources, and a URL a human can read
 * is worth keeping.
 *
 * Two clients are built from this, and the split matters: one carries the
 * parent's access token, the other carries nothing and is used by the session
 * manager itself. Without the split, refreshing a token would ask for a token,
 * and the recursion would only show up under load.
 */
export type ApiClient = ContractRouterClient<typeof apiContract>;

export const createClient = (getAccessToken?: () => Promise<string | null>): ApiClient => {
  const link = new OpenAPILink(apiContract, {
    url: environment.VITE_API_URL,
    // Required for the refresh cookie to travel at all. Without it the browser
    // sends no credentials cross-origin and every refresh fails silently.
    fetch: (request, init) => globalThis.fetch(request, { ...init, credentials: 'include' }),
    headers: async () => {
      if (getAccessToken === undefined) {
        return {};
      }

      const accessToken = await getAccessToken();
      return accessToken === null ? {} : { authorization: `Bearer ${accessToken}` };
    },
  });

  return createORPCClient(link);
};
